import { FC } from 'react';
import { ApolloError } from '@apollo/client/errors';
import { Button, Center, Icon, Stack, Text } from '@chakra-ui/react';
import { BiSearch } from 'react-icons/bi';
import { CiFileOff } from 'react-icons/ci';
import { Link } from 'react-router-dom';
import { ObjectNotFound } from '../../components/object-not-found/object-not-found';
import * as Routes from '../../constants/routes';
import { FileDetailsLayout } from './file-details-layout';

const isUnauthorizedError = (error?: ApolloError) =>
  error?.message === 'unauthorized';

type FileErrorStateProps = { error?: ApolloError };

export const FileErrorState: FC<FileErrorStateProps> = ({ error }) => {
  const testId = 'file-error-state';

  // The user doesn't have the correct role to access the file
  if (isUnauthorizedError(error)) {
    return (
      <FileDetailsLayout>
        <Center flex={1} data-testid={testId}>
          <ObjectNotFound
            icon={CiFileOff}
            title="Unauthorized"
            subtitle={
              <Stack>
                <Text>
                  Your account doesn't have permission to access this file.
                </Text>
                <Text>
                  Please contact an administrator for further assistance.
                </Text>
              </Stack>
            }
          />
        </Center>
      </FileDetailsLayout>
    );
  }

  // The user has the correct role, but the file could not be found
  // (the file has been deleted, or the user used a wrong file ID in the URL)
  return (
    <FileDetailsLayout>
      <Center flex={1} data-testid={testId}>
        <ObjectNotFound
          icon={CiFileOff}
          title="File not found"
          subtitle={
            <Stack>
              <Text>
                You can try{' '}
                <Button
                  as={Link}
                  to={Routes.SEARCH}
                  rightIcon={<Icon as={BiSearch} />}
                  colorScheme="blue"
                  size="lg"
                  variant="link"
                >
                  searching for the file
                </Button>
              </Text>
              <Text>
                If the file isn't found in the search, it may have been deleted.
              </Text>
            </Stack>
          }
        />
      </Center>
    </FileDetailsLayout>
  );
};
